/**
 * Engine: validation predicates.
 *
 * Combination-uniqueness check (no two branches with the same ancestry +
 * record) and the cheap "any node has a record anywhere in the tree" predicate
 * used to decide between emitting a real tree value or the empty
 * `{trees: [], winningRecordIds: []}`. Error messages embed entity labels when
 * supplied, falling back to "Level N" so the engine has no domain copy
 * hard-coded. Depth gating for add-child is inlined at `buildNodeMenu`.
 */
import type { RelationshipTreeNode } from '../System.RelationshipTree.types';
import { findNode } from './traversal';

export type CombinationValidationResult = {
  isValid: boolean;
  errorMessage?: string;
};

const getPathIds = (
  trees: RelationshipTreeNode[],
  nodeKey: string,
  entityHierarchy: string[],
): Array<string | undefined> => {
  const findPath = (
    nodes: RelationshipTreeNode[],
    ancestors: RelationshipTreeNode[],
    depth: number,
  ): Array<string | undefined> | null => {
    for (const node of nodes) {
      const currentAncestors = [...ancestors, node];
      if (node.key === nodeKey) {
        return currentAncestors.map((n) => n.entityRecordId);
      }
      if (node.children && depth < entityHierarchy.length - 1) {
        const result = findPath(node.children, currentAncestors, depth + 1);
        if (result) return result;
      }
    }
    return null;
  };
  return findPath(trees, [], 0) ?? [];
};

const getAllCombinations = (
  trees: RelationshipTreeNode[],
  maxDepth: number,
): Array<{ recordIds: string[]; leafKey: string }> => {
  const combinations: Array<{ recordIds: string[]; leafKey: string }> = [];
  const traverse = (
    nodes: RelationshipTreeNode[],
    ancestors: RelationshipTreeNode[],
    depth: number,
  ): void => {
    for (const node of nodes) {
      const currentAncestors = [...ancestors, node];
      const currentDepth = depth + 1;
      if (currentDepth === maxDepth || !node.children || node.children.length === 0) {
        const allRecordIds = currentAncestors.map((n) => n.entityRecordId);
        const hasAllRecords = allRecordIds.every((id) => id !== undefined);
        if (hasAllRecords && currentDepth === maxDepth) {
          combinations.push({
            recordIds: allRecordIds as string[],
            leafKey: node.key,
          });
        }
      }
      if (node.children && currentDepth < maxDepth) {
        traverse(node.children, currentAncestors, currentDepth);
      }
    }
  };
  traverse(trees, [], 0);
  return combinations;
};

export const validateCombinationUniqueness = (
  trees: RelationshipTreeNode[],
  nodeKey: string,
  newRecordId: string | undefined,
  entityHierarchy: string[],
  maxDepth: number,
  entityLabels?: Record<string, string>,
): CombinationValidationResult => {
  if (!newRecordId || entityHierarchy.length === 0) return { isValid: true };

  const node = findNode(trees, nodeKey);
  if (!node) return { isValid: true };

  const currentPathIds = getPathIds(trees, nodeKey, entityHierarchy);
  const nodeDepthIndex = currentPathIds.length - 1;
  if (nodeDepthIndex < 0) return { isValid: true };

  const proposedPathIds = [...currentPathIds];
  proposedPathIds[nodeDepthIndex] = newRecordId;

  if (proposedPathIds.some((id) => id === undefined)) return { isValid: true };
  if (proposedPathIds.length !== maxDepth) return { isValid: true };

  const allCombinations = getAllCombinations(trees, maxDepth);
  const isDuplicate = allCombinations.some((combo) => {
    if (combo.leafKey === nodeKey) return false;
    if (combo.recordIds.length !== proposedPathIds.length) return false;
    return combo.recordIds.every((id, index) => id === proposedPathIds[index]);
  });

  if (!isDuplicate) return { isValid: true };

  const labelParts = entityHierarchy.map(
    (entityId, index) => entityLabels?.[entityId] ?? `Level ${index + 1}`,
  );
  return {
    isValid: false,
    errorMessage: `This combo ${labelParts.join(', ')} already exists. Please select a different record.`,
  };
};

/**
 * Returns true when at least one node in the tree carries an
 * `entityRecordId`. Used by the field-level "any data?" check that decides
 * whether to persist the tree value or emit the empty
 * `{trees: [], winningRecordIds: []}`. A tree of empty placeholder nodes is
 * not considered to have data.
 */
export const hasValidData = (trees: RelationshipTreeNode[] | null | undefined): boolean => {
  if (!trees || trees.length === 0) return false;
  const walk = (nodes: RelationshipTreeNode[]): boolean => {
    for (const node of nodes) {
      if (node.entityRecordId) return true;
      if (node.children && walk(node.children)) return true;
    }
    return false;
  };
  return walk(trees);
};
